import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EMPLOYEMENT_STATUS,
  EMPLOYED_FOR,
  renderSection,
  formatIssueText,
} from "../utils/Constant";
import { addMonthsReliable, filterArrayByIssueType } from "../shared/Helpers";
import getIssues from "../app/services/getIssues";
import EditIcon from "../assets/img/Edit.svg";
import UpdateAtjData from "../components/UpdateATJData";
import getUser from "../app/services/getUser";
import { getToken, getDealAndToken, removeToken } from "./../shared/Helpers";
import getParagraphs from "../app/services/getParagraphs";
import generateCaseDetailLetter from "../app/services/generateCaseDetailLetter";
import Cycle from "../assets/img/cycle.svg";
import updateUser from "../app/services/updateUser";
import ErrorMessage from "../components/ErrorMessage";
import rotaGateway from "../app/services/rotaGateway";

function capitalizeFLetter(text) {
  return text[0].toUpperCase() + text.slice(1);
}

function PreviewLetter() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [statements, setStatements] = useState([]);
  const [formType, setFormType] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [issues, setIssues] = useState({
    other: [],
    employment: [],
    dismissal: [],
    redundancy: [],
  });
  const token = getToken();
  const dealAndToken = getDealAndToken();
  const { lastPage, dataInputs } = state || {};
  const [surveyData, setSurveyData] = useState(dataInputs || {});
  const [questionAnswers, setQuestionAnswer] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  const handleError = (error, type, description) => {
    console.error(description, error);
    setError({ error, type, description });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data } = await getUser(token);
        const updatedSurveyData = data.survey;

        if (updatedSurveyData.acas_notified === "no") {
          updatedSurveyData.date2 = "";
          updatedSurveyData.deadline2 = "";
          updatedSurveyData.date3 = "";
        }

        if (updatedSurveyData.received_acas === "no") {
          updatedSurveyData.date3 = "";
        }
        const questions = JSON.parse(updatedSurveyData.generatedQuestions);
        const additionalQuestions = questions
          .filter((q) => q.title && updatedSurveyData[q.name])
          .map(
            (q) =>
              `<p><b>${q.title}</b></p>\n\n<p>${
                updatedSurveyData[q.name] || "-"
              }</p>`
          )
          .join("\n");

        const manualQuestion = `<p><b>If there is anything else you need us to know, please write it here:</b></p>\n\n${
          updatedSurveyData.extra_question &&
          updatedSurveyData.extra_question.trim()
            ? updatedSurveyData.extra_question
            : "-"
        }`;

        const allQuestions = `${additionalQuestions}\n${manualQuestion}`;

        setQuestionAnswer(allQuestions);
        setSurveyData(updatedSurveyData);
      } catch (error) {
        handleError(error, 'load', "Error fetching user data");
      }
    };

    if (!token || !dealAndToken) {
      removeToken();
      navigate("/login");
    }

    fetchUserData();
    if (dataInputs) {
      setSurveyData((prevData) => {
        const updatedData = { ...prevData, ...dataInputs };
        if (updatedData.acas_notified === "no") {
          updatedData.date2 = "";
          updatedData.deadline2 = "";
          updatedData.date3 = "";
        }

        if (updatedData.received_acas === "no") {
          updatedData.date3 = "";
        }

        return updatedData;
      });
    }
  }, [dataInputs, token, dealAndToken, navigate]);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const response = await getIssues();
        const data = response.data.data;
        const issueTypes = [1, 2, 3, 4];

        const newIssues = issueTypes.reduce((acc, type) => {
          acc[type] = filterArrayByIssueType(data, type).map((issue) => ({
            value: issue.attributes.TopicId,
            text: issue.attributes.Name,
          }));
          return acc;
        }, {});

        const filterQuery =
          surveyData.statements_list && surveyData.statements_list.length > 0
            ? surveyData.statements_list
                .map((id) => `&filters[id][$in]=${encodeURIComponent(id)}`)
                .join("")
            : "&filters[id][$in]=";

        const { data: { data: paragraphsData } = {} } = await getParagraphs(
          filterQuery
        );

        setStatements(paragraphsData);
        setIssues({
          employment: newIssues[1],
          other: [{ value: "none", text: "None" }, ...newIssues[2]],
          dismissal: newIssues[3],
          redundancy: newIssues[4],
        });
      } catch (error) {
        handleError(error, 'load', "Error fetching issues");
      }
    };

    fetchIssues();
  }, [surveyData.statements_list]);

  const updateAnswer = (formType) => {
    setFormType(formType);
    setShowPopup(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onHide = () => {
    setShowPopup(false);
  };

  const back = () => {
    navigate(`${lastPage}`);
  };

  const generateAndShowLetter = async () => {
    setIsButtonDisabled(true);
    setIsSubmitting(true);
    try {
      const { data } = await getUser(token);

      const date1_1 = data.survey.date
        ? new Date(data.survey.date)
        : data.survey.date_last
        ? new Date(data.survey.date_last)
        : null;
      const date1_2 = data.survey.date1_2
        ? new Date(data.survey.date1_2)
        : null;

      let date1 = null; // Either termination date, or last date of discrimination / whistleblowing / health & safety - whichever is earliest
      if (date1_1 && date1_2) {
        if (date1_1 < date1_2) {
          date1 = date1_1;
        } else {
          date1 = date1_2;
        }
      } else {
        date1 = date1_1 || date1_2;
      }
      const date2 = data.survey.date2 ? new Date(data.survey.date2) : null; // Date ACAS Early Conciliation was started
      const date3 = data.survey.date3 ? new Date(data.survey.date3) : null; // Date ACAS Certificate was issued

      let time_limit = ""; // Dealline 1 (ACAS Early Conciliation Deadline)
      if (date1) {
        const dates = addMonthsReliable(date1, 3); // Use reliable month addition
        dates.setDate(dates.getDate() - 1); // Subtract 1 day from the 3-month date
        const mnth = `0${dates.getMonth() + 1}`.slice(-2);
        const day = `0${dates.getDate()}`.slice(-2);
        time_limit = `${dates.getFullYear()}-${mnth}-${day}`;
      } else {
        console.error("No valid date available");
      }

      
      let tribunal_deadline_date = ""; // Deadline 2 ( ET1 Deadline )

      // Check if all three dates are present before proceeding with the calculation
      if (date1 && date2 && date3) {
        const pauseStart = new Date(Math.max(date1, date2));
        const pauseDays = Math.max(0, Math.ceil((date3 - pauseStart) / (1000 * 60 * 60 * 24)));
        
        // Option A: date1 + 3 calendar months - 1 day + pauseDays
        const deadlineA = addMonthsReliable(date1, 3);
        deadlineA.setDate(deadlineA.getDate() - 1); // subtract 1 day
        deadlineA.setDate(deadlineA.getDate() + pauseDays); // add pauseDays

        // Option B: date3 + 1 calendar month - 1 day
        const deadlineB = addMonthsReliable(date3, 1);
        deadlineB.setDate(deadlineB.getDate() - 1);
        // Final deadline: latest of A and B
        const finalDeadline2 = deadlineA > deadlineB ? deadlineA : deadlineB;
        // Format as YYYY-MM-DD
        const mnth2 = `0${finalDeadline2.getMonth() + 1}`.slice(-2);
        const day2 = `0${finalDeadline2.getDate()}`.slice(-2);
        tribunal_deadline_date = `${finalDeadline2.getFullYear()}-${mnth2}-${day2}`;
      }

      const empIssues = surveyData.employment_issues && formatIssueText(
        surveyData.employment_issues,
        issues.employment
      );

      const othIssues =
        surveyData.other_issues &&
        formatIssueText(surveyData.other_issues, issues.other);
      const statementsList = statements
        .map((statement) => statement.attributes.Statement)
        .join(", ");
      const questions = JSON.parse(data.survey.generatedQuestions);
      const additionalQuestions = questions
        .map((q) => `Questions: ${q.title}\n\nAnswer: ${data.survey[q.name]}\n`)
        .join("\n");
      const manualQuestion = `Questions: If there is anything else you need us to know please write it here:\n\nAnswer: ${
        data.survey.extra_question ? data.survey.extra_question : "-"
      }`;
      const allQuestions = `${additionalQuestions}\n${manualQuestion}`;
      const optionalQuestion = `If there is anything else you need us to know please write it here:\n${
        data.survey.extra_question ? data.survey.extra_question : "-"
      }`;
      const baseUrl = window.location.origin;
      const formData = {
        userId: data.id,
        name: data.name,
        email: data.email,
        dealID: data.dealID,
        paragraph: data.survey.paragraph,
        date1: date1,
        time_limit: time_limit,
        time_limit2: tribunal_deadline_date,
        employmentIssues: empIssues,
        otherIssues: othIssues,
        statementsList: statementsList,
        additionalQuestions: allQuestions,
        optionalQuestion: optionalQuestion,
        empStatus: EMPLOYEMENT_STATUS[surveyData.employment_status],
        empLong: EMPLOYED_FOR[surveyData.how_long],
        wpLetterUrl: `${baseUrl}/client-review?customer=${dealAndToken}`,
        ...data.survey,
      };
      if (!data.checkedWpLetter) {
        await generateCaseDetailLetter(formData);
      }
      const dealDetails = await rotaGateway({
        method: 'POST',
        path: 'v1/update-case',
        body: formData,
      });
      const dealObject = dealDetails.data.related_objects;
      const isAtj = Object.values(dealObject.pipeline).some(
        (pipeline) => pipeline.name === "ATJ"
      );
      if (isAtj) {
        navigate(`/client-review?customer=${dealAndToken}`);
      } else {
        const payload = {
          dealID: data.dealID,
          wpLetterUrl: `${baseUrl}/internal-review?customer=${dealAndToken}`,
          stage: "trainee",
        };
        const updateField = {
          wpLetterReview: true,
          SentBackToClient: false,
        };
        await rotaGateway({
          method: 'POST',
          path: 'v1/submit-letter',
          body: payload,
        });
        await updateUser(token, updateField);
        navigate("/thank-you");
      }
    } catch (error) {
      handleError(error, 'submit', "Error generating and showing the letter");
      setIsButtonDisabled(false);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="update-letter">
        {!error && <>
          <div className="user-answers">
            <h2>Your information summary</h2>
            <p>
              Please scroll down to the end and press continue to generate your
              first draft legal letter
            </p>
            <h4>What happened and when?</h4>
            <div className="answers-container">
              <div className="answers">
                {surveyData.case_summary &&
                  renderSection(
                    "Description of your case",
                    surveyData.case_summary
                  )}
              </div>
            </div>
            <h4>Important Dates</h4>
            <div className="answers-container">
              <div className="answers">
                {surveyData.date
                  ? renderSection(
                      "Notice period",
                      `End date - ${surveyData.date
                        .split("-")
                        .reverse()
                        .join("-")}`
                    )
                  : renderSection("Notice period", "No")}

                {surveyData.date_last &&
                  renderSection(
                    "Notice period - End Date",
                    `End date - ${surveyData.date_last
                      .split("-")
                      .reverse()
                      .join("-")}`
                  )}

                {surveyData.date1_2 &&
                  renderSection(
                    "The latest incident",
                    surveyData.date1_2.split("-").reverse().join("-")
                  )}

                {surveyData.date2
                  ? renderSection(
                      "ACAS",
                      `Started on ${surveyData.date2
                        .split("-")
                        .reverse()
                        .join("-")}`
                    )
                  : renderSection("ACAS", "Not started")}

                {surveyData.date3
                  ? renderSection(
                      "ACAS certificate",
                      `Issued on ${surveyData.date3
                        .split("-")
                        .reverse()
                        .join("-")}`
                    )
                  : renderSection("ACAS", "Not issued yet")}
              </div>
              <div
                className="action"
                onClick={() => updateAnswer("timelimits")}
                style={{ cursor: "pointer" }}
              >
                <img src={EditIcon} alt="icon" />
                Change
              </div>
            </div>
            <h4>Other information</h4>
            <div className="answers-container">
              <div className="answers">
                {surveyData.employment_issues &&
                  renderSection(
                    "Employment issues",
                    formatIssueText(
                      surveyData.employment_issues,
                      issues.employment
                    )
                  )}

                {surveyData.other_issues &&
                  renderSection(
                    "Discrimination issues",
                    formatIssueText(surveyData.other_issues, issues.other)
                  )}
                {statements.length > 0 &&
                  renderSection(
                    "Statements",
                    statements
                      .map((statement) => statement.attributes.Statement)
                      .join(", ")
                  )}
                {surveyData.employment_status &&
                  renderSection(
                    "Are you still employed",
                    EMPLOYEMENT_STATUS[surveyData.employment_status]
                  )}

                {surveyData.how_long &&
                  renderSection(
                    "How long have you been in your job?",
                    EMPLOYED_FOR[surveyData.how_long]
                  )}
                {surveyData.total_salary &&
                  renderSection("Total annual salary", surveyData.total_salary)}

                {surveyData.offer_amount &&
                  renderSection("Offer received", surveyData.offer_amount)}

                {surveyData.age && renderSection("Age", surveyData.age)}

                {surveyData.gender &&
                  renderSection("Gender", capitalizeFLetter(surveyData.gender))}

                {surveyData.years_employed &&
                  renderSection(
                    "How many years at this job?",
                    surveyData.years_employed
                  )}

                {surveyData.start_date &&
                  renderSection("New job start date", surveyData.start_date)}

                {surveyData.company_name &&
                  renderSection(" Name of Employer (Company Name)", surveyData.company_name)}

                {surveyData.contact_name &&
                  renderSection(
                    "Name of Contact (HR or Director)", 
                    surveyData.contact_name
                  )}

                {surveyData.contact_email &&
                  renderSection(
                    "Email address we should send your letter to",
                    surveyData.contact_email
                  )}
              </div>
              {/* <div
                className="action"
                onClick={() => updateAnswer("questions")}
                style={{ cursor: "pointer" }}
              >
                <img src={EditIcon} alt="icon" />
                Change
              </div> */}
            </div>
            <h4>Questions and answers</h4>
            <div className="answers-container">
              <div
                className="answers"
                dangerouslySetInnerHTML={createMarkup(questionAnswers)}
              />
            </div>
          </div>
          <div className="action-buttons">
            <div>
              <div
                className="button back"
                onClick={back}
                style={{ cursor: "pointer" }}
              >
                Back
              </div>
              <div
                className="button continue"
                onClick={!isButtonDisabled ? generateAndShowLetter : null}
                style={{ cursor: isButtonDisabled ? "not-allowed" : "pointer" }}
                disabled={isButtonDisabled}
              >
                Continue
              </div>
            </div>
          </div>
        </> }
        <ErrorMessage {...error} componentName="PreviewLetter" />
      </div>
      {showPopup && <UpdateAtjData onHide={onHide} formType={formType} />}
      {isSubmitting && (
        <div className="submit-loader">
          <div>
            <img src={Cycle} width="auto" height="auto" alt="cycle" />
            <p>Please bear with, this could take a couple of minutes</p>
          </div>
        </div>
      )}
    </>
  );
}

export default PreviewLetter;
